import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import useGroupsConfig from "@/hooks/useGroupsConfig";

const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

/**
 * Pending GROUP invitations, rendered alongside team invitations. Accepting an invitation
 * sent by an owner/admin activates membership; a member's invitation to an
 * approval-required group becomes a membership request instead (GRP-DEC-085/086).
 */
export function GroupInvitations() {
  const qc = useQueryClient();
  const { visible } = useGroupsConfig();
  const [busyId, setBusyId] = React.useState(null);

  const { data } = useQuery({
    queryKey: ["group-invites-mine"],
    queryFn: async () => (await api.get("/group-invites/mine")).data,
    enabled: visible,
  });
  const invites = data?.invitations || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["group-invites-mine"] });
    qc.invalidateQueries({ queryKey: ["groups-mine"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const respond = async (inv, accept) => {
    setBusyId(inv.id);
    try {
      if (accept) {
        const { data: res } = await api.post(`/group-invites/${inv.id}/accept`, {
          rules_accepted: true,
        });
        toast.success(res.state === "active"
          ? `You've joined ${inv.group_name}.`
          : "Your request was sent to the organizers.");
      } else {
        await api.post(`/group-invites/${inv.id}/decline`);
        toast.success("Invitation declined.");
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This invitation is no longer available.");
      refresh();
    } finally { setBusyId(null); }
  };

  if (!visible || !invites.length) return null;

  return (
    <div className="mt-6 space-y-3" data-testid="group-invitations-list">
      <h2 className="text-lg font-[650] text-foreground">Group invitations</h2>
      {invites.map((inv) => (
        <Card key={inv.id} className="flex items-center gap-4 p-4"
              data-testid={`group-invitation-row-${inv.id}`}>
          <Avatar className="h-11 w-11 rounded-xl">
            <AvatarImage src={inv.group_image_url || undefined} alt="" />
            <AvatarFallback className="rounded-xl">{initials(inv.group_name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <Link to={inv.group_url || "#"} className="truncate text-sm font-[600] hover:underline">
              {inv.group_name}
            </Link>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="h-3 w-3" />{inv.member_count} members
              {inv.invited_by_name ? ` · invited by ${inv.invited_by_name}` : ""}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="ghost" disabled={busyId === inv.id}
                    onClick={() => respond(inv, false)}
                    data-testid={`group-invitation-decline-${inv.id}`}>
              <X className="mr-1 h-4 w-4" />Decline
            </Button>
            <Button size="sm" disabled={busyId === inv.id} onClick={() => respond(inv, true)}
                    data-testid={`group-invitation-accept-${inv.id}`}>
              {busyId === inv.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : <Check className="mr-1 h-4 w-4" />}Accept
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
